import { Alert, AppShell, Badge, Burger, Center, Group, Loader, NavLink, ScrollArea, Select, Text, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import {
  IconAlarm,
  IconAddressBook,
  IconArrowDownLeft,
  IconArrowUpRight,
  IconFileImport,
  IconFiles,
  IconHome,
  IconLogout,
  IconReceiptTax,
  IconSettings,
} from '@tabler/icons-react';
import { Navigate, NavLink as EnlaceRuta, Route, Routes, useLocation } from 'react-router';
import { api } from './api';
import { useSesion } from './sesion';
import type { Aviso } from './tipos';
import { PaginaAcceso, PaginaCambiarClave, PaginaConfigurar2fa, PaginaConfiguracionInicial } from './paginas/Acceso';
import { PaginaInicio } from './paginas/Inicio';
import { PaginaIngresos } from './paginas/Ingresos';
import { PaginaGastos } from './paginas/Gastos';
import { PaginaTerceros } from './paginas/Terceros';
import { PaginaDocumentos } from './paginas/Documentos';
import { PaginaFiscal } from './paginas/Fiscal';
import { PaginaAvisos } from './paginas/Avisos';
import { PaginaImportar } from './paginas/Importar';
import { PaginaAjustes } from './paginas/Ajustes';

const MENU = [
  { ruta: '/', texto: 'Inicio', icono: IconHome },
  { ruta: '/ingresos', texto: 'Ingresos y ventas', icono: IconArrowDownLeft },
  { ruta: '/gastos', texto: 'Gastos y compras', icono: IconArrowUpRight },
  { ruta: '/documentos', texto: 'Documentos', icono: IconFiles },
  { ruta: '/fiscal', texto: 'Impuestos', icono: IconReceiptTax },
  { ruta: '/avisos', texto: 'Avisos', icono: IconAlarm },
  { ruta: '/terceros', texto: 'Clientes y proveedores', icono: IconAddressBook },
  { ruta: '/importar', texto: 'Importar', icono: IconFileImport },
  { ruta: '/ajustes', texto: 'Ajustes', icono: IconSettings },
];

export function App() {
  const s = useSesion();
  if (s.cargando) {
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  }
  if (!s.configurado) return <PaginaConfiguracionInicial />;
  if (!s.usuario) return <PaginaAcceso />;
  if (s.usuario.debeCambiarClave) return <PaginaCambiarClave obligatorio />;
  if (s.seguridad?.exigir2fa && !s.usuario.totpActivo) return <PaginaConfigurar2fa />;
  if (!s.negocio) {
    return (
      <Center h="100vh">
        <Text>Tu usuario no tiene acceso a ningún negocio. Pide acceso a la persona titular.</Text>
      </Center>
    );
  }
  return <Estructura />;
}

function Estructura() {
  const s = useSesion();
  const [abierto, { toggle, close }] = useDisclosure();
  const ubicacion = useLocation();
  const avisos = useQuery({ queryKey: ['avisos', 'abiertos'], queryFn: () => api.get<Aviso[]>('/avisos'), refetchInterval: 5 * 60_000 });
  const pendientes = avisos.data?.filter((a) => a.estado === 'pendiente').length ?? 0;

  return (
    <AppShell header={{ height: 56 }} navbar={{ width: 250, breakpoint: 'sm', collapsed: { mobile: !abierto } }} padding="md">
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger opened={abierto} onClick={toggle} hiddenFrom="sm" size="sm" aria-label="Menú" />
            <img src="/icono.svg" alt="" width={28} height={28} />
            <Text fw={700}>GestionPYME</Text>
          </Group>
          {s.negocios.length > 1 ? (
            <Select
              size="xs"
              w={200}
              data={s.negocios.map((n) => ({ value: n.negocioId, label: n.esDemo ? `🧪 ${n.nombre}` : n.nombre }))}
              value={s.negocio!.negocioId}
              onChange={(v) => v && s.cambiarNegocio(v)}
              allowDeselect={false}
            />
          ) : (
            <Text size="sm" c="dimmed" truncate visibleFrom="xs">
              {s.negocio!.nombre}
            </Text>
          )}
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        <AppShell.Section grow component={ScrollArea}>
          {MENU.map((m) => (
            <NavLink
              key={m.ruta}
              component={EnlaceRuta}
              to={m.ruta}
              label={m.texto}
              leftSection={<m.icono size={18} stroke={1.6} />}
              active={m.ruta === '/' ? ubicacion.pathname === '/' : ubicacion.pathname.startsWith(m.ruta)}
              onClick={close}
              rightSection={
                m.ruta === '/avisos' && pendientes > 0 ? (
                  <Badge size="sm" color="orange" circle>
                    {pendientes}
                  </Badge>
                ) : undefined
              }
            />
          ))}
        </AppShell.Section>
        <AppShell.Section>
          <Text size="xs" c="dimmed" px="sm">
            {s.usuario!.nombre} · {s.negocio!.rol}
          </Text>
          <UnstyledButton onClick={() => void s.salir()} px="sm" py={6} w="100%">
            <Group gap="xs">
              <IconLogout size={16} />
              <Text size="sm">Salir</Text>
            </Group>
          </UnstyledButton>
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        {s.negocio!.esDemo && (
          <Alert color="yellow" variant="light" mb="md" title="Negocio de demostración">
            Estos datos son ficticios (basados en tu libro Excel) y sirven para ver cómo funciona la aplicación. No se envía nada a Telegram ni al correo. Cambia de negocio arriba para volver a los datos reales.
          </Alert>
        )}
        <Routes>
          <Route path="/" element={<PaginaInicio />} />
          <Route path="/ingresos/*" element={<PaginaIngresos />} />
          <Route path="/gastos/*" element={<PaginaGastos />} />
          <Route path="/documentos" element={<PaginaDocumentos />} />
          <Route path="/fiscal/*" element={<PaginaFiscal />} />
          <Route path="/avisos" element={<PaginaAvisos />} />
          <Route path="/terceros" element={<PaginaTerceros />} />
          <Route path="/importar" element={<PaginaImportar />} />
          <Route path="/ajustes/*" element={<PaginaAjustes />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell.Main>
    </AppShell>
  );
}
